import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';

import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import { StorefrontThemeContext } from '@/features/storefront/storefrontThemeContext';
import type { StorefrontTheme } from '@/features/storefront/themeSchema';

/**
 * The theme document the page is being rendered inside.
 *
 * The shell's, which in preview is the theme being tried on rather than the
 * published one; the published context otherwise, read from the query the
 * shell already fetched. Undefined only in the moment before either has
 * arrived.
 *
 * Every page that reaches for a colour, a section list or a type choice should
 * come through here rather than through the context query directly, or it will
 * quietly render the published theme inside a preview of a different one.
 */
export function useStorefrontTheme(): StorefrontTheme | undefined {
  const shellTheme = useContext(StorefrontThemeContext);
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const context = useQuery({
    queryKey: [...scope, 'context'],
    queryFn: () => source.context(),
    enabled: shellTheme === null,
  });

  return shellTheme ?? context.data?.theme;
}
